import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Switch, Paper, CircularProgress,
  Alert, Divider, Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../../services/firestoreService';

const DEFAULT_SPECIALTIES = [
  'Medicina General', 'Oncología', 'Cardiología',
  'Dermatología', 'Neurología', 'Cirugía',
];

const ManageSpecialties = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = async () => {
    setLoading(true);
    try {
      const data = await getCollection('specialties');
      setItems(data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    if (items.find((i) => i.name.toLowerCase() === newName.trim().toLowerCase())) {
      setSnackbar({ open: true, message: 'Ya existe esa especialidad', severity: 'warning' });
      return;
    }
    await addDocument('specialties', { name: newName.trim(), active: true });
    setNewName('');
    setSnackbar({ open: true, message: 'Especialidad agregada', severity: 'success' });
    load();
  };

  const handleToggle = async (item) => {
    await updateDocument('specialties', item.id, { active: !item.active });
    load();
  };

  const handleDelete = async (id) => {
    await deleteDocument('specialties', id);
    setSnackbar({ open: true, message: 'Especialidad eliminada', severity: 'info' });
    load();
  };

  const handleInitDefaults = async () => {
    for (const name of DEFAULT_SPECIALTIES) {
      if (!items.find((i) => i.name === name)) {
        await addDocument('specialties', { name, active: true });
      }
    }
    setSnackbar({ open: true, message: 'Especialidades por defecto creadas', severity: 'success' });
    load();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Especialidades Médicas</Typography>
          <Typography variant="body2" color="text.secondary">Usadas en el agendamiento online para filtrar profesionales</Typography>
        </Box>
        {items.length === 0 && (
          <Button variant="outlined" size="small" onClick={handleInitDefaults}>
            Inicializar valores por defecto
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label="Nueva especialidad"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          size="small"
          sx={{ flex: 1 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={!newName.trim()}>
          Agregar
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No hay especialidades configuradas</Typography>
          </Box>
        ) : (
          <List dense>
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Divider />}
                <ListItem>
                  <Switch size="small" checked={!!item.active} onChange={() => handleToggle(item)} sx={{ mr: 1 }} />
                  <ListItemText
                    primary={item.name}
                    secondary={item.active ? 'Activa en el agendamiento' : 'Inactiva'}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" color="error" size="small" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageSpecialties;
